/**
 * Utilities for processing HTML content to fix image URLs
 * Handles both preview and rendering of HTML content
 */

/**
 * Convert local image path to full URL
 * This is a string-based version for use in HTML processing
 * @param {string} imagePath - Local image path or full URL
 * @param {string} baseUrl - Base URL for constructing full paths
 * @returns {string} - Full image URL
 */
export const constructImageUrl = (imagePath, baseUrl = 'http://localhost:5000') => {
  if (!imagePath) return null;
  
  // If already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Remove leading slash if present
  const path = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return `${baseUrl}${path}`;
};

/**
 * Fix image URLs in HTML content (string-based processing)
 * Converts relative paths like /uploads/images/xyz.jpg to absolute URLs
 * @param {string} htmlContent - HTML content to process
 * @param {string} baseUrl - Base URL for image construction
 * @returns {string} - HTML content with fixed image URLs
 */
export const fixImageUrlsInHtmlString = (htmlContent, baseUrl = 'http://localhost:5000') => {
  if (!htmlContent) return '';
  
  // Use regex to find all img src attributes
  const imgRegex = /(<img[^>]+src=["'])([^"']+)(["'][^>]*>)/g;
  
  return htmlContent.replace(imgRegex, (match, before, src, after) => {
    // Skip if already a full URL
    if (src.startsWith('http://') || src.startsWith('https://')) {
      return match;
    }
    
    // Convert relative URLs to absolute
    const fixedUrl = constructImageUrl(src, baseUrl);
    return before + fixedUrl + after;
  });
};

/**
 * Create an HTML string with a base URL embedded for relative path resolution
 * This helps with iframe rendering to resolve relative image URLs
 * @param {string} htmlContent - HTML content
 * @param {string} baseUrl - Base URL to set as document base
 * @returns {string} - HTML with base tag injected
 */
export const addBaseUrlToHtml = (htmlContent, baseUrl = 'http://localhost:5000') => {
  if (!htmlContent) return '';
  
  // Check if HTML already has a head section
  if (htmlContent.includes('</head>')) {
    // Insert base tag before closing head
    return htmlContent.replace(
      '</head>',
      `<base href="${baseUrl}/">\n</head>`
    );
  } else if (htmlContent.includes('<body')) {
    // If no head, insert base tag before body
    return htmlContent.replace(
      '<body',
      `<head><base href="${baseUrl}/"></head><body`
    );
  } else {
    // Wrap HTML with head and base
    return `<html><head><base href="${baseUrl}/"></head><body>${htmlContent}</body></html>`;
  }
};

/**
 * Process HTML for preview in iframe with srcDoc
 * Fixes image URLs and adds base URL for relative path resolution
 * @param {string} htmlContent - HTML content
 * @param {string} baseUrl - Base URL for constructing full image paths
 * @returns {string} - Processed HTML ready for iframe srcDoc
 */
export const processHtmlForPreview = (htmlContent, baseUrl = 'http://localhost:5000') => {
  if (!htmlContent) return '';
  
  // First fix image URLs directly
  let processed = fixImageUrlsInHtmlString(htmlContent, baseUrl);
  
  // Then add base URL as additional fallback
  processed = addBaseUrlToHtml(processed, baseUrl);
  
  return processed;
};

const htmlProcessor = {
  constructImageUrl,
  fixImageUrlsInHtmlString,
  addBaseUrlToHtml,
  processHtmlForPreview
};

export default htmlProcessor;
